import FeatureGates from '@atlaskit/feature-gate-js-client';
import { IntlProvider, withIntlProvider } from '@post-office/placement-contexts/i18n';
import { type ComponentType } from 'react';

import {
	type AnyTranslatedMessagesResult,
	type I18nFactory,
	type LoadMessagesByTeam,
	type WithI18nFactory,
} from './types';
import { type AnyProps } from '../types';

export type { MessageLoader } from './types';

export const createI18n: I18nFactory = (loaders) => {
	const messagesLoader = createMessagesLoader(loaders);

	const I18n = ({ children }: { children: React.ReactNode }) => (
		<IntlProvider messagesLoader={messagesLoader}>{children}</IntlProvider>
	);

	return ({ children }) => {
		const isEnabled = FeatureGates.checkGate('post-office_placement-v2_i18n').valueOf();

		if (!isEnabled) {
			return <>{children}</>;
		}

		return <I18n>{children}</I18n>;
	};
};

export const createWithI18n: WithI18nFactory = (config) => {
	const messagesLoader = createMessagesLoader(config);

	return <T extends AnyProps>(component: ComponentType<T>): ComponentType<T> => {
		const isEnabled = FeatureGates.checkGate('post-office_placement-v2_i18n');

		if (!isEnabled) {
			return component;
		}

		return withIntlProvider({ messagesLoader }, component) as ComponentType<T>;
	};
};

export const createMessagesLoader: LoadMessagesByTeam =
	(...loaders) =>
	async (locale) => {
		const messagePromises = loaders.filter((e) => !!e).map((loader) => loader(locale));

		const loadedMessages = await Promise.allSettled(messagePromises);

		const translatedMessages: AnyTranslatedMessagesResult = { messages: {}, errors: [] };

		loadedMessages.forEach((result) => {
			if (result.status === 'rejected') {
				translatedMessages.errors.push(`Failed to load translations with ${result.reason}`);
				return;
			}

			translatedMessages.messages = { ...translatedMessages.messages, ...result.value };
		});

		return translatedMessages;
	};
